import React from 'react';

const experience = [
  {
    company: 'Amazon',
    role: 'Software Engineer II',
    location: 'Austin, TX',
    dates: 'Aug 2024 - Present',
    summary: [
      'Led regional expansion of grocery forecasting platforms across Japan and Singapore, scaling data pipelines to support international operations and improving forecast reliability for cross-regional planning teams.',
      'Architected and built a zero-touch Site Launch Automation platform using Python, Java, JavaScript, and AWS, eliminating manual onboarding workflows and saving ~10 engineering hours per launch.',
      'Designed and launched a Forecast Explainability Portal (React, Node.js, AWS Lambda) adopted by 10+ internal teams, enabling transparent model insights and reducing analysis time by 90%.',
      'Delivered high-performance analytics dashboards on AWS Glue and Athena, processing 5M+ rows daily while optimizing query pipelines to reduce load times by 70%.',
      'Integrated generative AI tools (OpenAI, Claude, Amazon Q) into operational workflows, automating repetitive tasks and reducing ongoing operational effort by 50%.',
    ],
  },
  {
    company: 'Walmart Global Tech',
    role: 'Sr. Software Engineer & Tech Lead',
    location: 'Dallas, TX',
    dates: 'Oct 2022 - Aug 2024',
    summary: [
      'Built a multi-tenant ML platform using React, Node.js, and cloud-native architecture, delivering analytics that informed over $1B in fiscal decisions across business units.',
      'Led front-end architecture and UI/UX strategy, improving product adoption and enabling faster, data-driven decision-making at scale.',
      'Streamlined CI/CD pipelines across multi-tenant deployments, increasing release reliability and accelerating delivery velocity.',
      'Pioneered an early generative AI solution for legal workflow optimization, introducing auditability and automation to reduce manual review overhead.',
      'Interviewed and evaluated engineering candidates, contributing to hiring decisions and strengthening team technical standards.',
    ],
  },
  {
    company: 'Fidelity Investments',
    role: 'Sr. Software Engineer & Tech Lead',
    location: 'Westlake, TX',
    dates: 'Oct 2020 - Oct 2022',
    summary: [
      'Led development of a C-suite transparency platform, delivering executive-facing insights that influenced strategic planning and business decisions.',
      'Owned architectural direction for full-stack systems, designing scalable, maintainable solutions that supported long-term product growth.',
      'Mentored and guided a 7-engineer team, conducting interviews, code reviews, and technical coaching to raise engineering quality and consistency.',
    ],
  },
  {
    company: 'Fidelity Investments',
    role: 'Software Engineer (Associate - Software Engineer)',
    location: 'Westlake, TX',
    dates: 'Jan 2018 - Mar 2019',
    summary: [
      'Refactored enterprise applications with OOP principles to improve maintainability and reduce code size by 80%.',
      'Delivered full-stack features with a front-end focus for Fidelity Solutions Team.',
    ],
  },
];

const education = [
  {
    school: 'Boston University',
    degree: 'Master of Science, Software Development',
    dates: '2019 - 2021',
    details: 'CGPA 3.60',
  },
  {
    school: 'Claflin University',
    degree: 'Bachelor of Science, Computer Science',
    dates: '2016 - 2017',
    details: 'CGPA 3.90',
  },
];

const impact = [
  {
    kicker: 'Experience',
    title: '8+ Years',
    subline: 'Tech Industry Experience',
  },
  {
    kicker: 'Companies',
    title: 'Amazon Walmart Fidelity',
    subline: 'Top-tier industry experience',
  },
  {
    kicker: 'Scope',
    title: 'Full-Stack',
    subline: 'UI → Backend → Infra → Data',
  },
  {
    kicker: 'Approach',
    title: 'Product-minded',
    subline: 'Architect and Builder',
  },
];

const Resume = () => {
  const [activeIndex, setActiveIndex] = React.useState(0);

  const toggleIndex = (index) => {
    setActiveIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <section className="resume__container resume-section">
      <div className="resume-hero">
        <div>
          <p className="resume-eyebrow">Resume</p>
          <h2 className="resume-title">Jushita Rahman</h2>
          <p className="resume-subtitle">Sr. Software Engineer</p>
          <p className="resume-summary">
            Full-stack engineer focused on modern web platforms, cloud-native architectures, and
            AI-enabled automation.
          </p>
        </div>
        <div className="resume-actions">
          <a className="resume-button primary" href="/assets/Jushita_Resume_2026.pdf" download>
            Download PDF
          </a>
          <a className="resume-button ghost" href="mailto:jushitaa@gmail.com">
            Let&apos;s Talk
          </a>
        </div>
      </div>

      <div className="resume-impact">
        {impact.map((item) => (
          <div key={`${item.kicker}-${item.title}`} className="impact-card">
            <p className="impact-kicker">{item.kicker}</p>
            <p className="impact-title">{item.title}</p>
            <p className="impact-subline">{item.subline}</p>
          </div>
        ))}
      </div>

      <div className="resume-grid">
        <div className="resume-column">
          <div className="resume-section-header">
            <h3>Experience</h3>
            <p>Tap a role to view highlights.</p>
          </div>
          <div className="resume-timeline">
            {experience.map((role, index) => (
              <div
                key={`${role.company}-${role.dates}`}
                className={`timeline-item ${activeIndex === index ? 'is-open' : ''}`}
              >
                <button
                  type="button"
                  className="timeline-toggle"
                  onClick={() => toggleIndex(index)}
                  aria-expanded={activeIndex === index}
                >
                  <div>
                    <p className="timeline-role">{role.role}</p>
                    <p className="timeline-company">
                      {role.company} · {role.location}
                    </p>
                  </div>
                  <span className="timeline-dates">{role.dates}</span>
                </button>
                <div className="timeline-panel">
                  <ul>
                    {role.summary.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="resume-column aside">
          <div className="resume-card">
            <h3>Education</h3>
            <div className="resume-stack">
              {education.map((item) => (
                <div key={item.school} className="resume-stack-item">
                  <p className="stack-title">{item.degree}</p>
                  <p className="stack-meta">{item.school}</p>
                  <p className="stack-meta">{item.dates}</p>
                  <p className="stack-detail">{item.details}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="resume-card">
            <h3>Contact</h3>
            <div className="contact-list">
              <a href="mailto:jushitaa@gmail.com">jushitaa@gmail.com</a>
              <a href="tel:+18177718668">+1 817-771-8668</a>
              <a href="https://linkedin.com/in/jushita" target="_blank" rel="noreferrer">
                linkedin.com/in/jushita
              </a>
              <a href="https://github.com/jushita" target="_blank" rel="noreferrer">
                github.com/jushita
              </a>
            </div>
          </div>

          <div className="resume-card">
            <h3>Leadership</h3>
            <p>
              Tech lead across cross-functional teams with a focus on product delivery, mentorship,
              and stakeholder alignment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resume;
